import React, { useState } from "react";
import { Box, Button, IconButton, Stack } from "@mui/material";

import { useMutation, useQuery } from "@tanstack/react-query";
import { createNewInstallation, fetchElionCrmOverview } from "../../scripts/api_v2/crm_client";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "../../hooks/use_backend";
import ReactTableWithSettings from "../../components/material_react_table/react_table_with_settings";
import MultiTabDialog from "../../components/material_react_table/multi_tab_dialog";
import DialogForm from "../../components/material_react_table/dialog_form";
import { FORM_ACTIONS } from "../../enums/form_actions_enum";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { REACT_TABLE_NAMES } from "../../enums/react_table_name_enum";
import { hasPermission, isComponentDisabledForUpdates } from "../login/login_utils";
import Privilege from "../../enums/privilege_enum";
import AddLinkIcon from "@mui/icons-material/AddLink";
import Snackbar from "../../scripts/snackbar";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import { BrusselsDateTimeStringToDate } from "../../scripts/utils";

function OverviewCrmTab({ required_privilege }) {
  const backend = useBackend();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTabs, setDialogTabs] = useState(null);
  const isDisabled = isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());

  const {
    data: elion_crm_overview,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.CRM_OVERVIEW, backend.fetch],
    queryFn: () => fetchElionCrmOverview(backend.fetch),
  });

  const { mutate: newInstallationMutation, isPending } = useMutation({
    mutationFn: async (data) =>
      await createNewInstallation(
        backend.fetch,
        !!data.isDebug,
        data.siteName,
        data.siteId,
        data.boxId,
        data.crmId,
        data.partnerCompanyId,
        data.action,
        data.ean_1,
        data.ean_2,
      ),
    onSuccess: () => {
      refetch();
      Snackbar.success("New installation success");
    },
    onError: (err, data, context) => {
      Snackbar.error("New installation failed: " + err);
    },
  });

  function sanitizeData(data) {
    if (!data || data.length <= 0) return [];
    else {
      const allKeys = Object.keys(data[0]);
      const dateKeys = [];
      allKeys.forEach((key) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes("date")) {
          dateKeys.push(key);
        }
      });
      return data.map((row) => {
        const sanitizedRow = { ...row };
        dateKeys.forEach((key) => {
          // The ELION CRM dates are currently unmodified from SF and seem to be in Brussels tz...
          sanitizedRow[key] = BrusselsDateTimeStringToDate(sanitizedRow[key]);
        });
        return sanitizedRow;
      });
    }
  }

  const sanitized_elion_crm_overview = React.useMemo(() => sanitizeData(elion_crm_overview), [elion_crm_overview]);

  const defaultVisibility = {
    elion_name: true,
    installer: true,
    wholesaler: true,
    ean_offtake: true,
    ean_injection: false,
    elion_start_date: false,
    elion_end_date: false,
    elion_effective_start: true,
    elion_effective_end: true,
    account_name_offtake: true,
    account_name_injection: true,
    ean_address_offtake: true,
    ean_address_injection: false,
    account_address_offtake: false,
    account_address_injection: false,
  };

  const defaultSorting = [{ id: "account_name_offtake", desc: false }];

  const columns = [
    {
      accessorKey: "elion_name",
      header: "ELION CONTRACT NAME",
      enableClickToCopy: false,
      Cell: ({ row }) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{row.original.elion_name}</span>
          {hasPermission(permissionMap, Privilege.CRM_CRM_OVERVIEW.getPrivilegeKey(), true) ? (
            <IconButton
              onClick={() => {
                handleAddLinkColumnAction(row.original);
              }}
            >
              <AddLinkIcon />
            </IconButton>
          ) : null}
        </div>
      ),
    },
    { accessorKey: "crm_id", header: "CRM ID", enableClickToCopy: true },
    { accessorKey: "account_name_offtake", header: "ACCOUNT NAME OFFTAKE", enableClickToCopy: true },
    {
      accessorKey: "account_name_injection",
      header: "ACCOUNT NAME INJECTION",
      enableClickToCopy: true,
      enableGlobalFilter: true,
    },
    { accessorKey: "installer", header: "INSTALLER", enableClickToCopy: true },
    { accessorKey: "wholesaler", header: "WHOLESALER", enableClickToCopy: true },
    { accessorKey: "ean_offtake", header: "EAN OFFTAKE", enableClickToCopy: true },
    { accessorKey: "ean_injection", header: "EAN INJECTION", enableClickToCopy: true },
    {
      accessorKey: "elion_start_date",
      header: "ELION_START_DATE",
      enableClickToCopy: true,
      enableGlobalFilter: false,
      filterVariant: "datetime-range",
      Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
      sortingFn: "customDateSortingFn",
      filterFn: "customDatetimeRangeFilter",
    },
    {
      accessorKey: "elion_end_date",
      header: "ELION_END_DATE",
      enableClickToCopy: true,
      enableGlobalFilter: false,
      filterVariant: "datetime-range",
      Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
      sortingFn: "customDateSortingFn",
      filterFn: "customDatetimeRangeFilter",
    },
    {
      accessorKey: "elion_effective_start",
      header: "ELION_EFFECTIVE_START_DATE",
      enableClickToCopy: true,
      enableGlobalFilter: false,
      filterVariant: "datetime-range",
      Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
      sortingFn: "customDateSortingFn",
      filterFn: "customDatetimeRangeFilter",
    },
    {
      accessorKey: "elion_effective_end",
      header: "ELION_EFFECTIVE_END_DATE",
      enableClickToCopy: true,
      enableGlobalFilter: false,
      filterVariant: "datetime-range",
      Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
      sortingFn: "customDateSortingFn",
      filterFn: "customDatetimeRangeFilter",
    },
    { accessorKey: "ean_address_offtake", header: "EAN ADDRESS OFFTAKE", enableClickToCopy: true },
    { accessorKey: "ean_address_injection", header: "EAN ADDRESS INJECTION", enableClickToCopy: true },
    { accessorKey: "account_address_offtake", header: "ACCOUNT ADDRESS OFFTAKE", enableClickToCopy: true },
    { accessorKey: "account_address_injection", header: "ACCOUNT ADDRESS INJECTION", enableClickToCopy: true },
  ];

  const handleAddLinkColumnAction = (row) => {
    setDialogTabs([
      {
        label: "New and Link",
        component: (
          <DialogForm
            isPending={isPending}
            selectedRow={row}
            onClose={() => setIsDialogOpen(false)}
            dialogAction={FORM_ACTIONS.NEW_INSTALLATION_FOR_CRM}
            handleConfirm={handleInstallationContractActionConfirm}
          />
        ),
      },
      {
        label: "Search and Link",
        component: (
          <DialogForm
            isPending={isPending}
            selectedRow={row}
            onClose={() => setIsDialogOpen(false)}
            dialogAction={FORM_ACTIONS.SEARCH_SITE_FOR_CRM}
            handleConfirm={handleInstallationContractActionConfirm}
          />
        ),
      },
    ]);
    setIsDialogOpen(true);
  };

  const handleInstallationContractActionConfirm = async (selectedRow, dialogAction, dialogData) => {
    newInstallationMutation({
      siteId: dialogData["site_id"],
      siteName: dialogData["site_name"],
      boxId: dialogData.box_id,
      isDebug: !!dialogData.is_debug,
      crmId: selectedRow ? selectedRow.crm_id : null,
      partnerCompanyId: dialogData[FORM_ACTIONS.DRAFT_INSTALLATION_WITH_PARTNER_PRIVILEGE.fnDataAccessKey],
      action: dialogAction.action,
      ean_1: dialogData.ean_1,
      ean_2: dialogData.ean_2,
    });

    setIsDialogOpen(false);
  };

  function draftInstallationButton() {
    return (
      <Stack direction="row">
        {hasPermission(permissionMap, Privilege.CRM_CRM_OVERVIEW.getPrivilegeKey(), true) ? (
          <>
            <Button
              sx={{
                alignItems: "center",
                borderRadius: "3px", // Small rounded corners
                border: "0.5px solid", // Optional: add a border
                color: "text.secondary",
                textTransform: "none", // Prevent text from being capitalized
              }}
              aria-label="create draft installation"
              startIcon={<DesignServicesIcon fontSize="small" />} // Adds the icon before text
              onClick={handleDraftInstallationOnClick}
            >
              Draft Installation
            </Button>
          </>
        ) : null}
      </Stack>
    );
  }

  function handleDraftInstallationOnClick() {
    const dialogAction = hasPermission(permissionMap, Privilege.PARTNER_COMPANY_OVERVIEW.getPrivilegeKey(), true)
      ? FORM_ACTIONS.DRAFT_INSTALLATION_WITH_PARTNER_PRIVILEGE
      : FORM_ACTIONS.DRAFT_INSTALLATION;
    setDialogTabs([
      {
        label: "Draft installation",
        component: (
          <DialogForm
            isPending={isPending}
            selectedRow={{}} // Pass selected row data
            onClose={() => setIsDialogOpen(false)} // Close the dialog
            dialogAction={dialogAction} // Your action
            handleConfirm={handleInstallationContractActionConfirm}
          />
        ),
      },
    ]);
    setIsDialogOpen(true); // Open the dialog
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <ReactTableWithSettings
          unique_table_key={REACT_TABLE_NAMES.CRM_OVERVIEW}
          columns={columns}
          data={sanitized_elion_crm_overview}
          error={error}
          isLoading={isLoading}
          defaultVisibility={defaultVisibility}
          defaultSorting={defaultSorting}
          topToolbarComponent={draftInstallationButton()}
        />
      </Box>
      {isDisabled ? null : (
        <MultiTabDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} dialogTabs={dialogTabs} />
      )}
    </>
  );
}

export default OverviewCrmTab;
